/**
 * usePrivacyGovernance Hook
 * PROJ-082 - Privacy Governance Canvas Integration
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import type {
  NodePrivacyGovernance,
  ComplianceScoreBreakdown,
  DpiaAssessment,
  ApplicableLawsResponse,
} from '@/types/privacy-governance';
import {
  getNodeGovernance,
  updateNodeGovernance,
  assessDpia,
  getComplianceScore,
  getApplicableLaws,
} from '@/lib/privacyGovernanceApi';

// Helper to check if a string is a valid UUID
const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

interface UsePrivacyGovernanceReturn {
  governance: NodePrivacyGovernance | null;
  loading: boolean;
  error: Error | null;
  score: ComplianceScoreBreakdown | null;
  dpiaAssessment: DpiaAssessment | null;
  applicableLaws: ApplicableLawsResponse | null;
  refresh: () => Promise<void>;
  update: (data: Partial<NodePrivacyGovernance>) => Promise<void>;
}

export function usePrivacyGovernance(nodeId: string | null): UsePrivacyGovernanceReturn {
  const [governance, setGovernance] = useState<NodePrivacyGovernance | null>(null);
  const [score, setScore] = useState<ComplianceScoreBreakdown | null>(null);
  const [dpiaAssessment, setDpiaAssessment] = useState<DpiaAssessment | null>(null);
  const [applicableLaws, setApplicableLaws] = useState<ApplicableLawsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isPersistedNode = nodeId ? isValidUUID(nodeId) : false;

  const refresh = useCallback(async () => {
    if (!nodeId || !isPersistedNode) {
      setGovernance(null);
      setScore(null);
      setDpiaAssessment(null);
      setApplicableLaws(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [govData, scoreData, dpiaData, lawsData] = await Promise.all([
        getNodeGovernance(nodeId).catch(() => null),
        getComplianceScore(nodeId).catch(() => null),
        assessDpia(nodeId).catch(() => null),
        getApplicableLaws(nodeId).catch(() => null),
      ]);

      setGovernance(govData || { nodeId });
      setScore(scoreData);
      setDpiaAssessment(dpiaData);
      setApplicableLaws(lawsData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al cargar gobernanza de privacidad');
      setError(error);
      toast({
        title: 'Error al cargar datos de privacidad',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [nodeId, isPersistedNode]);

  const update = useCallback(
    async (data: Partial<NodePrivacyGovernance>): Promise<void> => {
      if (!nodeId || !isPersistedNode) return;

      try {
        const updated = await updateNodeGovernance(nodeId, data);
        setGovernance(updated);

        // PROJ-004: Add delay before refresh to ensure cache invalidation
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Refresh score and DPIA after update
        const [scoreData, dpiaData] = await Promise.all([
          getComplianceScore(nodeId).catch(() => null),
          assessDpia(nodeId).catch(() => null),
        ]);

        setScore(scoreData);
        setDpiaAssessment(dpiaData);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error al actualizar gobernanza');
        toast({
          title: 'Error al actualizar privacidad',
          description: error.message,
          variant: 'destructive',
        });
      }
    },
    [nodeId, isPersistedNode]
  );

  // Load on mount and when nodeId changes
  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    governance,
    loading,
    error,
    score,
    dpiaAssessment,
    applicableLaws,
    refresh,
    update,
  };
}
